import { useState, useEffect, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Progress } from "./ui/progress";
import { ScrollArea } from "./ui/scroll-area";
import {
  FileText,
  Check,
  Loader2,
  Shield,
  Clock,
  CheckCircle2,
  ArrowLeft,
  Eye,
  RotateCcw,
  X,
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import useDealDocuments from "../hooks/useDealDocuments";
import usePresignedUrl from "../hooks/usePresignedUrl";
import { uploadToS3 } from "../lib/s3Upload";

const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const isWholesaleAgreement = (doc) =>
  doc?.key === "wholesale_agreement" || doc?.label === "Wholesale Agreement";

const formatSignatureDate = () =>
  new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const formatPercent = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "50";
  return Number.isInteger(numericValue)
    ? String(numericValue)
    : numericValue.toFixed(2);
};

const readPercentSetting = (data, keys, fallback) => {
  for (const key of keys) {
    const value = data?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const dataUrlToFile = (dataUrl, filename) => {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/png";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
};

const WholesaleAgreementPreview = ({
  deal,
  cashflowPercent,
  agentPercent,
  signaturePreviewUrl,
  cashFlow4USignature,
}) => {
  const propertyAddress = [
    deal?.property_address,
    [deal?.city, deal?.state, deal?.zip_code].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  const isSigned = Boolean(signaturePreviewUrl);

  const signatureDate = isSigned ? formatSignatureDate() : null;
  const agentName = deal?.reserved_by_name || "_______________________";

  return (
    <article className="space-y-5 text-sm leading-6 text-muted-foreground">
      <h3 className="text-xl font-bold text-center text-foreground">
        Wholesale Contract Agreement
      </h3>

      <p>
        This is a Wholesale Contract Agreement which is an agreement between the
        Wholesaler hereby designated as{" "}
        <strong className="text-foreground">Cash Flow 4U</strong> and the Real
        Estate Agent, Broker and/or Investor hereby designated as{" "}
        <strong className="text-foreground">{agentName}</strong> regarding the
        Real Estate Purchase contract dated{" "}
        <strong className="text-foreground">{signatureDate}</strong> for the
        property located{" "}
        <strong className="text-foreground">
          {propertyAddress || "_______________________"}
        </strong>
        .
      </p>

      <section className="space-y-2">
        <h3 className="font-semibold text-foreground">Assignment Fee Split</h3>
        <p>
          Cash Flow 4U agrees to split any assignment fee received from assignee
          with the Real Estate Agent in which the Real Estate Agent represents
          the Wholesaler/Assignor as a Buyer Broker or Buyer's Agent. The
          wholesale fee shall be split with Cash Flow 4U receiving{" "}
          <strong className="text-foreground">
            {formatPercent(cashflowPercent)}%
          </strong>{" "}
          percent and the Real Estate Agent, Broker, or Investor receiving{" "}
          <strong className="text-foreground">
            {formatPercent(agentPercent)}%
          </strong>{" "}
          percent of the wholesale fee payable as a concession in escrow.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold text-foreground">
          Validity and Cancellation
        </h3>
        <p>
          This agreement will be valid until further notice and can be canceled
          at any time by Cash Flow 4U.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold text-foreground">
          Assignment of Contract
        </h3>
        <ol className="list-decimal pl-5 space-y-2">
          <li>
            Wholesalers are authorized to assign all the contractual rights and
            obligations of such a contract only to the designated person of
            Wholesaler's Choice.
          </li>
          <li>
            Wholesaler reserves the right to close escrow only based on such
            contractual terms of the State Contract in the Wholesaler's name or
            another entity's name.
          </li>
          <li>
            Wholesaler agrees to pay the assignment fee to the agent/investor
            who provided the property for acquisition, regardless of whether
            Wholesaler closes the transaction in their personal name or another
            entity, upon receiving such fee from the acquiring investor.
          </li>
          <li>
            Escrow is to be determined and subject to modification per the state
            purchase contract.
          </li>
        </ol>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold text-foreground">Governing Law</h3>
        <p>
          This agreement will be governed by the laws of the State in which the
          subject property is located.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold text-foreground">Entire Agreement</h3>
        <p>
          This 2-page agreement represents the entire understanding between the
          parties and supersedes all prior negotiations, understandings, and
          agreements.
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold text-foreground">
          Mediation and Arbitration
        </h3>
        <p>
          By signing, parties of this agreement agree to resolve any disputes
          regarding this agreement first by mediation, which is to begin within
          30 days of such party's notification of the dispute to the other party
          via email or via the disputing party's designated legal counsel. Upon
          there being no resolution of such dispute in the mediation proceeding,
          arbitration may then be immediately initiated following such mediation
          proceeding by either of the parties, with such arbitration proceeding
          to be resolved in the State in which the property is located. The
          physical location of mediation and arbitration proceedings is to be
          determined. In the event any party does not respond to a request for
          mediation, or denies such request within 72 hours of such request, the
          requesting party may initiate arbitration by any means necessary.
        </p>
      </section>

      <p className="pt-2">
        By signing below, the parties agree to the terms and conditions set
        forth in this Wholesale Contract Agreement.
      </p>

      <div className="space-y-6 border-t pt-4 text-foreground">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4">
          <div>
            <p className="font-semibold">Agent's/Investor Signature:</p>
            <div className="mt-3 h-20 border-b flex items-end pb-2">
              {signaturePreviewUrl && (
                <img
                  src={signaturePreviewUrl}
                  alt="Uploaded signature preview"
                  className="max-h-16 max-w-[220px] object-contain"
                />
              )}
            </div>
            <p className="mt-2 text-sm">{agentName}</p>
          </div>
          <p className="self-end text-sm">
            Date: {signatureDate || "_______________"}
          </p>
        </div>

        <div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4">
            <div>
              <p className="font-semibold">
                Cash Flow 4U Wholesaler Signature:
              </p>

              <div className="mt-3 h-20 border-b flex items-end pb-2">
                {cashFlow4USignature && (
                  <img
                    src={cashFlow4USignature}
                    alt="Cash Flow 4U Signature"
                    className="max-h-16 max-w-[220px] object-contain"
                  />
                )}
              </div>
            </div>

            <p className="self-end text-sm">
              Date: {signatureDate || "_______________"}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
};

const DocumentChecklistModal = ({
  open,
  onOpenChange,
  deal,
  onUpdate,
  onDocumentSigned,
}) => {
  const [signingDoc, setSigningDoc] = useState(null);
  const [documentsState, setDocumentsState] = useState({});
  const [viewStep, setViewStep] = useState(null); // null | { doc, step: "view" | "sign" }
  const [agreed, setAgreed] = useState(false);
  const [legalName, setLegalName] = useState("");
  const [nameError, setNameError] = useState("");
  const [loading, setLoading] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [appSettingsData, setAppSettingsData] = useState({});
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState(null);
  const [signatureUploadMeta, setSignatureUploadMeta] = useState(null);
  const [signatureUploadProgress, setSignatureUploadProgress] = useState(0);
  const [signatureMode, setSignatureMode] = useState("draw");
  const [uploadedSignatureFile, setUploadedSignatureFile] = useState(null);
  const [uploadedSignaturePreviewUrl, setUploadedSignaturePreviewUrl] = useState(null);
  const signaturePadRef = useRef(null);
  const { user } = useAuth();
  const { documents: dealDocuments, loadingDocs } = useDealDocuments();

  useEffect(() => {
    return () => {
      if (uploadedSignaturePreviewUrl) {
        URL.revokeObjectURL(uploadedSignaturePreviewUrl);
      }
    };
  }, [uploadedSignaturePreviewUrl]);

  useEffect(() => {
    if (signatureMode === "upload") {
      signaturePadRef.current?.clear();
      setSignatureFile(null);
      setSignaturePreviewUrl(null);
    } else {
      setUploadedSignatureFile(null);
      if (uploadedSignaturePreviewUrl) {
        URL.revokeObjectURL(uploadedSignaturePreviewUrl);
        setUploadedSignaturePreviewUrl(null);
      }
    }
  }, [signatureMode]);

  const [cashFlow4USignature, setCashFlow4USignature] = useState(null);
  const { openPresignedUrl, presignedLoading } = usePresignedUrl();

  // Wholesale split percentages are stored on the deal (set at submit time).
  // If the deal doesn't carry them (legacy data) fall back to app settings,
  // then to the 50/50 template default.
  const cashflowPercent =
    deal?.cashflow_percent ??
    readPercentSetting(
      appSettingsData,
      ["cashflow_percent", "cash_flow_4u_percentage", "cash_flow_4U_percentage"],
      50,
    );
  const agentPercent =
    deal?.agent_percent ??
    readPercentSetting(
      appSettingsData,
      ["agent_percent", "wholesale_fee_percentage"],
      50,
    );

  const handleViewDocument = async (docItem, dealId) => {
     if (docItem.key === "wholesale_agreement") {
    const signedHere = documentsState?.wholesale_agreement;
    const storedOnDeal = deal?.documents?.wholesale_agreement?.storage_path;
    if (!signedHere && !storedOnDeal) {
      toast.info("The Wholesale Contract Agreement is generated once you sign it. Click Sign to review and sign now.");
      return;
    }
  } else {
    const storedOnDeal = deal?.documents?.[docItem.key]?.storage_path;
    if (!storedOnDeal) {
      toast.info(`${docItem.label || "This document"} wasn't uploaded.`);
      return;
    }
  }

    // 1) If the wholesale_agreement was signed in this checklist, the freshly
    //    generated PDF is stored in `deal.documents.wholesale_agreement` —
    //    prefer that direct key.
    // 2) Else fall back to the original deal-attached document by name.
    const generatedPath =
      deal?.documents?.[docItem.key]?.storage_path ||
      documentsState[docItem.key]?.storage_path;

    setViewingDoc(docItem.key);
    try {
      if (generatedPath) {
        await openPresignedUrl({ key: generatedPath });
      } else {
        await openPresignedUrl({ key: docItem.key, dealId });
      }
    } finally {
      setViewingDoc(null);
    }
  };

  useEffect(() => {
    if (!open) {
      setViewStep(null);
    }
  }, [open]);

  useEffect(() => {
    if (deal?.documents_signed) {
      setDocumentsState(deal.documents_signed);
    }
  }, [deal]);

  useEffect(() => {
    if (!open) return;
    const fetchAppSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`, {
          withCredentials: true,
          headers: getAuthHeaders(),
        });

        setAppSettingsData(response.data?.data || {});

        setCashFlow4USignature(response.data?.cash_flow_4U_signature);
      } catch {
        setAppSettingsData({});
      }
    };
    fetchAppSettings();
  }, [open]);

  const signedCount = Object.values(documentsState).filter(
    (v) => v !== null,
  ).length;
  const progress =
    dealDocuments.length > 0 ? (signedCount / dealDocuments.length) * 100 : 0;
  const allComplete =
    dealDocuments.length > 0 && signedCount === dealDocuments.length;

  const validateLegalName = (value) => {
    const nextValue = value.trim();

    if (!user?.name) {
      setNameError("Your account name is not available yet.");
      return false;
    }

    if (!nextValue) {
      setNameError("Enter your legal name to continue.");
      return false;
    }

    if (nextValue !== user.name) {
      setNameError("Name must exactly match your legal name");
      return false;
    }

    setNameError("");
    return true;
  };

  const handleStartSign = (doc) => {
    setViewStep({ doc, step: "view" });
    setAgreed(false);
    setLegalName("");
    setNameError("");
    setSignatureFile(null);
    setSignaturePreviewUrl(null);
    setSignatureUploadMeta(null);
    setSignatureUploadProgress(0);
    setSignatureMode("draw");
    setUploadedSignatureFile(null);
    setUploadedSignaturePreviewUrl(null);
  };

  const handleSignatureDrawn = () => {
    const pad = signaturePadRef.current;
    if (!pad || pad.isEmpty()) return;

    const dataUrl = pad.getCanvas().toDataURL("image/png");
    setSignaturePreviewUrl(dataUrl);
    setSignatureFile(dataUrlToFile(dataUrl, "wholesale-signature.png"));
    setSignatureUploadMeta(null);
    setSignatureUploadProgress(0);
  };

  const clearSignature = () => {
    signaturePadRef.current?.clear();
    setSignatureFile(null);
    setSignaturePreviewUrl(null);
    setSignatureUploadMeta(null);
    setSignatureUploadProgress(0);
    setUploadedSignatureFile(null);
    setUploadedSignaturePreviewUrl(null);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    const MAX_SIZE = 5 * 1024 * 1024;
    const ALLOWED_TYPES = ["image/png", "image/jpeg"];

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Only PNG and JPG files are allowed");
      return;
    }

    if (file.size > MAX_SIZE) {
      toast.error("File size must be 5MB or less");
      return;
    }

    if (uploadedSignaturePreviewUrl) {
      URL.revokeObjectURL(uploadedSignaturePreviewUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    setUploadedSignatureFile(file);
    setUploadedSignaturePreviewUrl(previewUrl);

    try {
      const uploadMeta = await uploadToS3(file, "documents", setSignatureUploadProgress);
      setSignatureUploadMeta(uploadMeta);
      setSignatureUploadProgress(0);
    } catch (error) {
      setUploadedSignatureFile(null);
      setUploadedSignaturePreviewUrl(null);
      setSignatureUploadMeta(null);
      const message = error.response?.data?.detail || error.message || "Failed to upload signature";
      toast.error(message);
    }
  };

  const handleConfirmSign = async () => {
    if (!deal || !viewStep || !agreed) return;
    if (!validateLegalName(legalName)) return;

    const docKey = viewStep.doc.key;
    const isWholesaleDoc = isWholesaleAgreement(viewStep.doc);

    setLoading(true);
    try {
      let nextSignatureUploadMeta = signatureUploadMeta;
      if (isWholesaleDoc) {
        const activeFile = signatureMode === "draw" ? signatureFile : uploadedSignatureFile;
        if (!activeFile && !nextSignatureUploadMeta) {
          toast.error("Add your digital signature before signing");
          setLoading(false);
          return;
        }

        if (!nextSignatureUploadMeta) {
          nextSignatureUploadMeta = await uploadToS3(
            activeFile,
            "documents",
            setSignatureUploadProgress,
          );
          setSignatureUploadMeta(nextSignatureUploadMeta);
        }
      }

      const response = await axios.post(
        `${API}/deals/${deal.deal_id}/sign-document`,
        {
          deal_id: deal.deal_id,
          document_type: docKey,
          signed_legal_name: legalName.trim(),
          signature_storage_path: nextSignatureUploadMeta?.storage_path,
          cashflow_percent: Number(cashflowPercent),
          agent_percent: Number(agentPercent),
        },
        { withCredentials: true, headers: getAuthHeaders() },
      );

      setDocumentsState(response.data.documents_signed);
      toast.success(`${viewStep.doc.label} signed successfully!`);
      setViewStep(null);
      setAgreed(false);
      setLegalName("");
      setNameError("");
      setSignatureFile(null);
      setSignaturePreviewUrl(null);
      setSignatureUploadMeta(null);
      setSignatureUploadProgress(0);
      setSignatureMode("draw");
      setUploadedSignatureFile(null);
      setUploadedSignaturePreviewUrl(null);

      if (onUpdate) onUpdate(response.data);
      if (onDocumentSigned) onDocumentSigned(response.data);

      if (response.data.all_documents_complete) {
        toast.success("All documents signed! Deal is now in closing.");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to sign document";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!deal) return null;

  if (loadingDocs) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogTitle className="sr-only">Loading documents</DialogTitle>
          <DialogDescription className="sr-only">
            Loading document list.
          </DialogDescription>
          <div className="py-12 flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading documents...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (viewStep) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[900px] lg:max-w-[1000px] max-h-[92vh] overflow-y-auto">
          {/* VIEW STEP */}
          <div style={{ display: viewStep.step === "view" ? "block" : "none" }}>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {viewStep.doc.label}
              </DialogTitle>
              <DialogDescription>{viewStep.doc.description}</DialogDescription>
            </DialogHeader>

            <ScrollArea className="h-[60vh] border rounded-lg p-4 bg-muted/30 mt-4">
              {isWholesaleAgreement(viewStep.doc) ? (
                <WholesaleAgreementPreview
                  deal={deal}
                  cashflowPercent={cashflowPercent}
                  agentPercent={agentPercent}
                  signaturePreviewUrl={signaturePreviewUrl || uploadedSignaturePreviewUrl || null}
                  cashFlow4USignature={cashFlow4USignature}
                  isSigned={!!documentsState?.wholesale_agreement}
                />
              ) : (
                <pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground">
                  {viewStep.doc.content}
                </pre>
              )}
            </ScrollArea>

            <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setViewStep(null)}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Checklist
              </Button>
              <Button
                onClick={() => setViewStep({ ...viewStep, step: "sign" })}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
              >
                <Shield className="w-4 h-4 mr-2" />
                Proceed to Sign
              </Button>
            </DialogFooter>
          </div>

          {/* SIGN STEP */}
          <div
            style={
              viewStep.step === "sign"
                ? {}
                : {
                    visibility: "hidden",
                    position: "absolute",
                    pointerEvents: "none",
                  }
            }
          >
            <DialogHeader>
              <div className="mx-auto w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-blue-500" />
              </div>
              <DialogTitle className="text-center text-xl font-heading">
                Sign {viewStep.doc.label}
              </DialogTitle>
              <DialogDescription className="text-center">
                Review and accept the terms to sign this document
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 my-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="font-medium text-foreground">
                  {deal.property_address}
                </p>
                <p className="text-sm text-muted-foreground">
                  {deal.city}, {deal.state} {deal.zip_code}
                </p>
              </div>

              {isWholesaleAgreement(viewStep.doc) && (
                <div className="space-y-3 p-4 border rounded-lg bg-card">
                  <Label className="text-sm font-medium">
                    Digital signature
                  </Label>

                  <Tabs value={signatureMode} onValueChange={setSignatureMode} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="draw">Draw Signature</TabsTrigger>
                      <TabsTrigger value="upload">Upload Signature</TabsTrigger>
                    </TabsList>

                    <TabsContent value="draw" className="mt-3">
                      <div
                        id="signature-pad"
                        className="rounded-md border bg-white overflow-hidden"
                        data-testid="doc-signature-pad"
                      >
                        <SignatureCanvas
                          ref={signaturePadRef}
                          penColor="black"
                          canvasProps={{ className: "w-full h-44" }}
                          onEnd={handleSignatureDrawn}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-muted-foreground">
                          Draw your signature in the box.
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={clearSignature}
                          disabled={loading || !signatureFile}
                          data-testid="doc-clear-signature"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Clear
                        </Button>
                      </div>
                    </TabsContent>

                    <TabsContent value="upload" className="mt-3 space-y-3">
                      <Input
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(file);
                        }}
                        disabled={loading || !!signatureUploadMeta}
                        data-testid="doc-upload-signature"
                      />
                      {uploadedSignaturePreviewUrl && (
                        <div className="rounded-md border bg-muted/30 p-3 relative">
                          <img
                            src={uploadedSignaturePreviewUrl}
                            alt="Uploaded signature preview"
                            className="max-h-24 max-w-full object-contain"
                          />
                          {signatureUploadMeta && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (uploadedSignaturePreviewUrl) {
                                  URL.revokeObjectURL(uploadedSignaturePreviewUrl);
                                }
                                setUploadedSignatureFile(null);
                                setUploadedSignaturePreviewUrl(null);
                                setSignatureUploadMeta(null);
                              }}
                              className="absolute top-1 right-1 h-6 w-6 p-0"
                              data-testid="doc-remove-uploaded-signature"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Upload a PNG or JPG file (max 5MB).
                      </p>
                    </TabsContent>
                  </Tabs>

                  {loading && signatureUploadProgress > 0 && (
                    <Progress value={signatureUploadProgress} className="h-2" />
                  )}
                </div>
              )}

              <div className="flex items-start space-x-3 p-4 border rounded-lg bg-card">
                <Checkbox
                  id={`agree-${viewStep.doc.key}`}
                  checked={agreed}
                  onCheckedChange={setAgreed}
                  className="mt-1"
                  data-testid="doc-agree-checkbox"
                />
                <Label
                  htmlFor={`agree-${viewStep.doc.key}`}
                  className="text-sm leading-relaxed cursor-pointer"
                >
                  I have read, understood, and agree to be bound by all terms of
                  the {viewStep.doc.label}. I understand that my electronic
                  signature has the same legal effect as a handwritten
                  signature.
                </Label>
              </div>

              <div className="space-y-2 p-4 border rounded-lg bg-card">
                <Label
                  htmlFor="signed-legal-name"
                  className="text-sm font-medium"
                >
                  Enter your legal name
                </Label>
                <Input
                  id="signed-legal-name"
                  value={legalName}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setLegalName(nextValue);
                    validateLegalName(nextValue);
                  }}
                  placeholder={user?.name || "Your full legal name"}
                  data-testid="doc-signed-legal-name"
                />
                {nameError && (
                  <p className="text-xs text-red-500">{nameError}</p>
                )}
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setViewStep({ ...viewStep, step: "view" })}
                className="flex-1"
                data-testid="doc-view-again-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Document
              </Button>
              <Button
                onClick={handleConfirmSign}
                disabled={
                  !agreed ||
                  !legalName.trim() ||
                  !!nameError ||
                  loading ||
                  (isWholesaleAgreement(viewStep.doc) &&
                    !signatureFile &&
                    !uploadedSignatureFile &&
                    !signatureUploadMeta)
                }
                className="flex-1 bg-green-500 hover:bg-green-600"
                data-testid="doc-confirm-sign-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />I Agree & Sign
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Sub-view: Sign Document
  if (viewStep?.step === "sign") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-[550px]"
          data-testid="document-sign-modal"
        >
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-blue-500" />
            </div>
            <DialogTitle className="text-center text-xl font-heading">
              Sign {viewStep.doc.label}
            </DialogTitle>
            <DialogDescription className="text-center">
              Review and accept the terms to sign this document
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="font-medium text-foreground">
                {deal.property_address}
              </p>
              <p className="text-sm text-muted-foreground">
                {deal.city}, {deal.state} {deal.zip_code}
              </p>
            </div>

{isWholesaleAgreement(viewStep.doc) && (
              <div className="space-y-3 p-4 border rounded-lg bg-card">
                <Label className="text-sm font-medium">
                  Digital signature
                </Label>

                <Tabs value={signatureMode} onValueChange={setSignatureMode} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="draw">Draw Signature</TabsTrigger>
                    <TabsTrigger value="upload">Upload Signature</TabsTrigger>
                  </TabsList>

                  <TabsContent value="draw" className="mt-3">
                    <div
                      id="signature-pad"
                      className="rounded-md border bg-white overflow-hidden"
                      data-testid="doc-signature-pad"
                    >
                      <SignatureCanvas
                        ref={signaturePadRef}
                        penColor="black"
                        canvasProps={{
                          className: "w-full h-32",
                        }}
                        onEnd={handleSignatureDrawn}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-muted-foreground">
                        Draw your signature in the box.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearSignature}
                        disabled={loading || !signatureFile}
                        data-testid="doc-clear-signature"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Clear
                      </Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="upload" className="mt-3 space-y-3">
                    <Input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                      }}
                      disabled={loading || !!signatureUploadMeta}
                      data-testid="doc-upload-signature"
                    />
                    {uploadedSignaturePreviewUrl && (
                      <div className="rounded-md border bg-muted/30 p-3 relative">
                        <img
                          src={uploadedSignaturePreviewUrl}
                          alt="Uploaded signature preview"
                          className="max-h-24 max-w-full object-contain"
                        />
                        {signatureUploadMeta && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (uploadedSignaturePreviewUrl) {
                                URL.revokeObjectURL(uploadedSignaturePreviewUrl);
                              }
                              setUploadedSignatureFile(null);
                              setUploadedSignaturePreviewUrl(null);
                              setSignatureUploadMeta(null);
                            }}
                            className="absolute top-1 right-1 h-6 w-6 p-0"
                            data-testid="doc-remove-uploaded-signature"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Upload a PNG or JPG file (max 5MB).
                    </p>
                  </TabsContent>
                </Tabs>

                {loading && signatureUploadProgress > 0 && (
                  <Progress value={signatureUploadProgress} className="h-2" />
                )}
              </div>
            )}

            <div className="flex items-start space-x-3 p-4 border rounded-lg bg-card">
              <Checkbox
                id={`agree-${viewStep.doc.key}`}
                checked={agreed}
                onCheckedChange={setAgreed}
                className="mt-1"
                data-testid="doc-agree-checkbox"
              />
              <Label
                htmlFor={`agree-${viewStep.doc.key}`}
                className="text-sm leading-relaxed cursor-pointer"
              >
                I have read, understood, and agree to be bound by all terms of
                the {viewStep.doc.label}. I understand that my electronic
                signature has the same legal effect as a handwritten signature.
              </Label>
            </div>

            <div className="space-y-2 p-4 border rounded-lg bg-card">
              <Label
                htmlFor="signed-legal-name"
                className="text-sm font-medium"
              >
                Enter your legal name
              </Label>
              <Input
                id="signed-legal-name"
                value={legalName}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setLegalName(nextValue);
                  validateLegalName(nextValue);
                }}
                placeholder={user?.name || "Your full legal name"}
                data-testid="doc-signed-legal-name"
              />

              {nameError && <p className="text-xs text-red-500">{nameError}</p>}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setViewStep({ ...viewStep, step: "view" })}
              className="flex-1"
              data-testid="doc-view-again-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Document
            </Button>
            <Button
              onClick={handleConfirmSign}
              disabled={
                !agreed ||
                !legalName.trim() ||
                !!nameError ||
                loading ||
                (isWholesaleAgreement(viewStep.doc) &&
                  !signatureFile &&
                  !uploadedSignatureFile &&
                  !signatureUploadMeta)
              }
              className="flex-1 bg-green-500 hover:bg-green-600"
              data-testid="doc-confirm-sign-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />I Agree & Sign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Main view: Document Checklist
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[820px] lg:max-w-[900px] max-h-[92vh] overflow-y-auto"
        data-testid="document-checklist-modal"
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-heading flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Document Signing Checklist
              </DialogTitle>
              <DialogDescription>
                Complete all documents to proceed with closing
              </DialogDescription>
            </div>
            {allComplete ? (
              <div className="flex items-center gap-2 text-green-500 bg-green-500/10 px-3 py-1 rounded-full">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm font-medium">Complete</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-orange-500 bg-orange-500/10 px-3 py-1 rounded-full">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">In Progress</span>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Deal Info */}
        <div className="bg-muted/50 rounded-lg p-4 mb-4">
          <p className="font-medium text-foreground">{deal.property_address}</p>
          <p className="text-sm text-muted-foreground">
            {deal.city}, {deal.state} {deal.zip_code}
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Progress</span>
            <span className="text-sm font-medium">
              {signedCount} of {dealDocuments.length} signed
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Document List */}
    {/* Document List */}
<div className="space-y-3">
  {dealDocuments.map((doc, index) => {
    const docItem = {
      key: doc.name,
      label: doc.label,
      description: doc.description || doc.label,
      content: doc.content || "",
    };
    const isSigned = documentsState[docItem.key] !== null;
    const isWholesaleAgreement = docItem.key === "wholesale_agreement";
    const storedOnDeal = deal?.documents?.[docItem.key]?.storage_path;
    const isAvailable = isWholesaleAgreement
      ? isSigned || !!storedOnDeal
      : !!storedOnDeal;

    return (
      <div
        key={docItem.key}
        className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
          isSigned
            ? "bg-green-500/5 border-green-500/20"
            : "bg-card border-border hover:border-blue-500/50"
        }`}
        data-testid={`document-item-${docItem.key}`}
      >
        <div className="flex items-center gap-4">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isSigned ? "bg-green-500" : "bg-muted"
            }`}
          >
            {isSigned ? (
              <Check className="w-4 h-4 text-white" />
            ) : (
              <span className="text-sm font-medium text-muted-foreground">
                {index + 1}
              </span>
            )}
          </div>
          <div>
            <p
              className={`font-medium ${isSigned ? "text-green-600 dark:text-green-400" : "text-foreground"}`}
            >
              {docItem.label}
            </p>
            <p className="text-xs text-muted-foreground">
              {docItem.description}
            </p>
            {isSigned && documentsState[docItem.key] && (
              <p className="text-xs text-green-500 mt-1">
                Signed on{" "}
                {new Date(
                  typeof documentsState[docItem.key] === "string"
                    ? documentsState[docItem.key]
                    : documentsState[docItem.key]?.signed_at ||
                        documentsState[docItem.key],
                ).toLocaleDateString()}
              </p>
            )}
                  {!isWholesaleAgreement && !isAvailable && (
          <p className="text-xs text-muted-foreground mt-1 italic">
          Wasn't provided
          </p>
        )}
        {isWholesaleAgreement && !isAvailable && (
          <p className="text-xs text-muted-foreground mt-1 italic">
            Not signed yet
          </p>
)}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              handleViewDocument(docItem, deal.deal_id)
            }
            disabled={!isAvailable || viewingDoc === docItem.key || presignedLoading}
            data-testid={`view-${docItem.key}-btn`}
          >
            {viewingDoc === docItem.key ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            View
          </Button>
         {!isSigned && (
            <Button
              size="sm"
              onClick={() => handleStartSign(docItem)}
              className="bg-blue-500 hover:bg-blue-600"
              disabled={!isWholesaleAgreement && !isAvailable}
              data-testid={`sign-${docItem.key}-btn`}
            >
              <Shield className="w-4 h-4 mr-1" />
              Sign
            </Button>
          )}
          {isSigned && (
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          )}
        </div>
      </div>
    );
  })}
</div>

        {/* Status Message */}
        {allComplete && (
          <div className="mt-6 bg-green-500/10 border border-green-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-green-600 dark:text-green-400">
                  All Documents Complete!
                </p>
                <p className="text-sm text-green-600/80 dark:text-green-400/80 mt-1">
                  Your deal is now in the closing process. You will be notified
                  of next steps.
                </p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentChecklistModal;
